import { useState, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useListLocations,
  useListLocationAvailability,
  useCreateLocationAvailability,
  useDeleteLocationAvailability,
  useUpdateLocationAvailability,
  useUpdateLocation,
  useGetGymScheduleSync,
  getListLocationAvailabilityQueryKey,
  getListLocationsQueryKey,
  type Location,
  type LocationAvailabilityBlock,
} from "@workspace/api-client-react";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Clock, Plus, Trash2, Building2, Copy, Pencil, Link2 } from "lucide-react";

const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const DAY_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const TIME_OPTIONS = Array.from({ length: 48 }, (_, i) => {
  const totalMinutes = i * 30;
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  const period = h < 12 ? "AM" : "PM";
  const displayH = h === 0 ? 12 : h > 12 ? h - 12 : h;
  const label = `${displayH}:${m.toString().padStart(2, "0")} ${period}`;
  const value = `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
  return { label, value };
});

function DayScheduleRow({
  day,
  dayIndex,
  blocks,
  locationId,
  onAdded,
  onDeleted,
  onUpdated,
  previousDayName,
  previousDayBlocks,
  readOnly,
}: {
  day: string;
  dayIndex: number;
  blocks: LocationAvailabilityBlock[];
  locationId: string;
  onAdded: () => void;
  onDeleted: () => void;
  onUpdated: () => void;
  previousDayName?: string;
  previousDayBlocks?: LocationAvailabilityBlock[];
  readOnly?: boolean;
}) {
  const { toast } = useToast();
  const [startTime, setStartTime] = useState("09:00");
  const [endTime, setEndTime] = useState("18:00");
  const [adding, setAdding] = useState(false);
  const [copying, setCopying] = useState(false);
  const [editingBlockId, setEditingBlockId] = useState<string | null>(null);
  const [editStartTime, setEditStartTime] = useState("09:00");
  const [editEndTime, setEditEndTime] = useState("18:00");

  const dayBlocks = blocks
    .filter(b => b.dayOfWeek === dayIndex && b.isActive)
    .sort((a, b) => a.startTime.localeCompare(b.startTime));
  const hasPreviousDayBlocks = (previousDayBlocks ?? []).length > 0;

  const createMutation = useCreateLocationAvailability();
  const deleteMutation = useDeleteLocationAvailability();
  const updateMutation = useUpdateLocationAvailability();

  const handleAdd = async () => {
    if (startTime >= endTime) {
      toast({ title: "Invalid range", description: "Start time must be before end time", variant: "destructive" });
      return;
    }
    try {
      await createMutation.mutateAsync({
        locationId,
        data: { dayOfWeek: dayIndex, startTime, endTime },
      });
      onAdded();
      setAdding(false);
    } catch {
      toast({ title: "Error", description: "Failed to add time block", variant: "destructive" });
    }
  };

  const handleDelete = async (blockId: string) => {
    try {
      await deleteMutation.mutateAsync({ locationId, id: blockId });
      onDeleted();
    } catch {
      toast({ title: "Error", description: "Failed to remove time block", variant: "destructive" });
    }
  };

  const handleStartEdit = (block: LocationAvailabilityBlock) => {
    setEditingBlockId(block.id);
    setEditStartTime(block.startTime);
    setEditEndTime(block.endTime);
  };

  const handleSaveEdit = async () => {
    if (!editingBlockId) return;
    if (editStartTime >= editEndTime) {
      toast({ title: "Invalid range", description: "Start time must be before end time", variant: "destructive" });
      return;
    }
    try {
      await updateMutation.mutateAsync({
        locationId,
        id: editingBlockId,
        data: { dayOfWeek: dayIndex, startTime: editStartTime, endTime: editEndTime },
      });
      onUpdated();
      setEditingBlockId(null);
      toast({ title: "Time block updated" });
    } catch {
      toast({ title: "Error", description: "Failed to update time block", variant: "destructive" });
    }
  };

  const handleCopyPreviousDay = async (checked: boolean) => {
    if (!previousDayBlocks || previousDayBlocks.length === 0) return;
    setCopying(true);
    try {
      if (checked) {
        for (const block of dayBlocks) {
          await deleteMutation.mutateAsync({ locationId, id: block.id });
        }
        for (const block of previousDayBlocks) {
          await createMutation.mutateAsync({
            locationId,
            data: { dayOfWeek: dayIndex, startTime: block.startTime, endTime: block.endTime },
          });
        }
        toast({ title: "Copied", description: `Schedule copied from ${previousDayName}` });
      } else {
        for (const block of dayBlocks) {
          await deleteMutation.mutateAsync({ locationId, id: block.id });
        }
        toast({ title: "Cleared", description: `${day} schedule cleared` });
      }
      onAdded();
    } catch {
      toast({ title: "Error", description: "Failed to copy schedule", variant: "destructive" });
    } finally {
      setCopying(false);
    }
  };

  return (
    <div className="py-3 border-b border-white/5 last:border-0">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-semibold text-white w-24">{day}</span>
        {dayBlocks.length === 0 && !adding && (
          <Badge variant="outline" className="text-[10px] border-white/10 text-muted-foreground">
            Closed
          </Badge>
        )}
        {!readOnly && (
          <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setAdding(true)}>
            <Plus className="w-3 h-3 mr-1" /> Add
          </Button>
        )}
      </div>

      {!readOnly && previousDayName && hasPreviousDayBlocks && (
        <div className="flex items-center gap-2 ml-2 mb-2">
          <Checkbox
            id={`copy-prev-${dayIndex}`}
            checked={
              dayBlocks.length > 0 &&
              previousDayBlocks!.length > 0 &&
              dayBlocks.length === previousDayBlocks!.length &&
              previousDayBlocks!.every(pb =>
                dayBlocks.some(db => db.startTime === pb.startTime && db.endTime === pb.endTime)
              )
            }
            onCheckedChange={(checked) => handleCopyPreviousDay(!!checked)}
            disabled={copying || createMutation.isPending || deleteMutation.isPending}
          />
          <label
            htmlFor={`copy-prev-${dayIndex}`}
            className="text-xs text-muted-foreground cursor-pointer select-none flex items-center gap-1"
          >
            <Copy className="w-3 h-3" />
            Same as {DAY_NAMES[dayIndex - 1]}
          </label>
        </div>
      )}

      {dayBlocks.map(block => (
        <div key={block.id} className="ml-2 mb-1.5">
          {!readOnly && editingBlockId === block.id ? (
            <div className="flex items-center gap-2">
              <Select value={editStartTime} onValueChange={setEditStartTime}>
                <SelectTrigger className="h-8 w-[100px] text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  {TIME_OPTIONS.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="text-muted-foreground text-xs">to</span>
              <Select value={editEndTime} onValueChange={setEditEndTime}>
                <SelectTrigger className="h-8 w-[100px] text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  {TIME_OPTIONS.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button size="sm" className="h-8 text-xs" onClick={handleSaveEdit} disabled={updateMutation.isPending}>
                Save
              </Button>
              <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => setEditingBlockId(null)}>
                Cancel
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Clock className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
              <span className="text-sm text-white">
                {formatTimeDisplay(block.startTime)} — {formatTimeDisplay(block.endTime)}
              </span>
              {!readOnly && (
                <>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 ml-auto text-muted-foreground hover:text-primary hover:bg-primary/10"
                    onClick={() => handleStartEdit(block)}
                    disabled={updateMutation.isPending}
                  >
                    <Pencil className="w-3 h-3" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                    onClick={() => handleDelete(block.id)}
                    disabled={deleteMutation.isPending}
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </>
              )}
            </div>
          )}
        </div>
      ))}

      {!readOnly && adding && (
        <div className="flex items-center gap-2 ml-2 mt-2">
          <Select value={startTime} onValueChange={setStartTime}>
            <SelectTrigger className="h-8 w-[100px] text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-h-60">
              {TIME_OPTIONS.map(t => (
                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="text-muted-foreground text-xs">to</span>
          <Select value={endTime} onValueChange={setEndTime}>
            <SelectTrigger className="h-8 w-[100px] text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-h-60">
              {TIME_OPTIONS.map(t => (
                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button size="sm" className="h-8 text-xs" onClick={handleAdd} disabled={createMutation.isPending}>
            Save
          </Button>
          <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => setAdding(false)}>
            Cancel
          </Button>
        </div>
      )}
    </div>
  );
}

function formatTimeDisplay(time: string): string {
  const [h, m] = time.split(":").map(Number);
  const period = h < 12 ? "AM" : "PM";
  const displayH = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${displayH}:${m.toString().padStart(2, "0")} ${period}`;
}

function LocationAvailabilityEditor({ location, scheduleSyncEnabled, primaryLocationName }: { location: Location; scheduleSyncEnabled: boolean; primaryLocationName?: string }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const isSynced = scheduleSyncEnabled && !location.isPrimary && location.useSharedSchedule;
  const isPrimaryWithSync = scheduleSyncEnabled && location.isPrimary;

  const { data: blocks = [], isLoading } = useListLocationAvailability(location.id);

  const updateLocationMutation = useUpdateLocation();

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: getListLocationAvailabilityQueryKey(location.id) });
  }, [queryClient, location.id]);

  const handleToggleUseSharedSchedule = async (checked: boolean) => {
    try {
      await updateLocationMutation.mutateAsync({
        id: location.id,
        data: { useSharedSchedule: checked },
      });
      queryClient.invalidateQueries({ queryKey: getListLocationsQueryKey(location.gymId) });
      invalidate();
      toast({ title: checked ? "Using shared schedule" : "Using custom schedule" });
    } catch {
      toast({ title: "Error", description: "Failed to update sync setting", variant: "destructive" });
    }
  };

  return (
    <div className="space-y-4">
      {scheduleSyncEnabled && !location.isPrimary && (
        <div className="flex items-center justify-between p-3 rounded-lg bg-white/[0.03] border border-white/5">
          <div>
            <p className="text-sm font-semibold text-white">Use shared schedule</p>
            <p className="text-xs text-muted-foreground mt-0.5">Mirror the primary location's hours</p>
          </div>
          <Switch
            checked={location.useSharedSchedule}
            onCheckedChange={handleToggleUseSharedSchedule}
            disabled={updateLocationMutation.isPending}
          />
        </div>
      )}

      {isSynced && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-primary/5 border border-primary/20 text-sm text-muted-foreground">
          <Link2 className="w-4 h-4 text-primary shrink-0" />
          <p>Schedule synced with <span className="text-white font-semibold">{primaryLocationName || "primary location"}</span>. Turn off shared schedule to edit independently.</p>
        </div>
      )}

      {isPrimaryWithSync && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-primary/5 border border-primary/20 text-sm text-muted-foreground">
          <Link2 className="w-4 h-4 text-primary shrink-0" />
          <p>Changes here will propagate to all synced locations.</p>
        </div>
      )}

      <div>
        {isLoading ? (
          <div className="flex justify-center py-8">
            <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="rounded-lg bg-white/[0.02] border border-white/5 px-4">
            {DAY_NAMES.map((day, i) => {
              const prevIndex = i - 1;
              const previousDayBlocks = prevIndex >= 0
                ? blocks.filter(b => b.dayOfWeek === prevIndex && b.isActive)
                : undefined;
              return (
                <DayScheduleRow
                  key={i}
                  day={DAY_SHORT[i]}
                  dayIndex={i}
                  blocks={blocks}
                  locationId={location.id}
                  onAdded={invalidate}
                  onDeleted={invalidate}
                  onUpdated={invalidate}
                  previousDayName={prevIndex >= 0 ? DAY_SHORT[prevIndex] : undefined}
                  previousDayBlocks={previousDayBlocks}
                  readOnly={isSynced}
                />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export type OperatingHoursData = {
  locationId: string;
  mode: "CUSTOM";
  blocks: { dayOfWeek: number; startTime: string; endTime: string }[];
};

export function OperatingHoursPanel({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { user } = useAuth();
  const gymId = user?.gymId;
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null);

  const { data: locations = [], isLoading: locationsLoading } = useListLocations(gymId ?? "", {
    query: { enabled: !!gymId },
  });

  const { data: syncData } = useGetGymScheduleSync(gymId ?? "", {
    query: { enabled: !!gymId },
  });

  const scheduleSyncEnabled = syncData?.scheduleSyncEnabled ?? false;
  const activeLocations = locations.filter(l => l.isActive);
  const selectedLocation = activeLocations.find(l => l.id === selectedLocationId) ?? activeLocations[0] ?? null;
  const primaryLocation = activeLocations.find(l => l.isPrimary);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg bg-background border-white/5 overflow-y-auto">
        <SheetHeader className="pb-4">
          <SheetTitle className="text-white flex items-center gap-2">
            <Clock className="w-5 h-5 text-primary" />
            Operating Hours
          </SheetTitle>
          <SheetDescription>
            Set when your gym is open for private lesson bookings.
          </SheetDescription>
        </SheetHeader>

        {locationsLoading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : activeLocations.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center gap-3">
            <Building2 className="w-10 h-10 text-muted-foreground" />
            <p className="text-muted-foreground">No active locations found.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {activeLocations.length > 1 && (
              <Select
                value={selectedLocation?.id ?? ""}
                onValueChange={setSelectedLocationId}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select location" />
                </SelectTrigger>
                <SelectContent>
                  {activeLocations.map(loc => (
                    <SelectItem key={loc.id} value={loc.id}>
                      <div className="flex items-center gap-2">
                        <Building2 className="w-3.5 h-3.5" />
                        {loc.name}
                        {loc.isPrimary && (
                          <Badge variant="outline" className="text-[9px] ml-1">Primary</Badge>
                        )}
                        {scheduleSyncEnabled && !loc.isPrimary && loc.useSharedSchedule && (
                          <Badge variant="secondary" className="text-[9px] ml-1">Synced</Badge>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {selectedLocation && (
              <div>
                {activeLocations.length <= 1 && (
                  <div className="flex items-center gap-2 mb-4 text-sm text-muted-foreground">
                    <Building2 className="w-4 h-4" />
                    <span className="font-semibold text-white">{selectedLocation.name}</span>
                  </div>
                )}
                <LocationAvailabilityEditor
                  key={selectedLocation.id}
                  location={selectedLocation}
                  scheduleSyncEnabled={scheduleSyncEnabled}
                  primaryLocationName={primaryLocation?.name}
                />
              </div>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

export function useOperatingHoursForCalendar(locationId?: string) {
  const { user } = useAuth();
  const gymId = user?.gymId;

  const { data: locations = [] } = useListLocations(gymId ?? "", {
    query: { enabled: !!gymId },
  });

  const targetLocation = locationId
    ? locations.find(l => l.id === locationId && l.isActive)
    : (locations.find(l => l.isPrimary && l.isActive) ?? locations.find(l => l.isActive));

  const { data: blocks = [] } = useListLocationAvailability(targetLocation?.id ?? "", {
    query: {
      enabled: !!targetLocation,
    },
  });

  if (!targetLocation) return { mode: "CUSTOM" as const, blocks: [], locationName: "" };

  return {
    mode: "CUSTOM" as const,
    blocks: blocks.filter(b => b.isActive),
    locationName: targetLocation.name,
  };
}
